import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Mail,
  User,
  Save,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  Info,
  Link2,
  Eye,
  Bold,
  Italic,
  List,
  AlignLeft,
  Sparkles,
  Search,
  ChevronDown,
  ChevronRight,
  Settings,
  Send,
  BarChart3,
  MousePointerClick,
  ExternalLink,
  Tag,
  Trash2,
  Undo,
  Image,
  Link,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  MailOpen,
  Reply,
  Ban,
  UserMinus,
  ThumbsDown,
  Loader2,
  Edit3,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import WriteEmailWithAIModal from './WriteEmailWithAIModal';
import RichEmailEditor from './RichEmailEditor';
import MergeFieldInput from './MergeFieldInput';
import { formatEmailForEditor } from '../../utils/writeEmailAIUtils';
import { countWords, stripHtmlForCount } from '../../utils/richEmailEditorUtils';
import {
  formatTestEmailErrorMessage,
  formatTestEmailSuccessMessage,
  isAutomationTestEmailSuccess,
  parseTestEmailInput,
  sendAutomationTestEmail,
  validateTestEmailInput,
} from '../../utils/automationTestEmailApi';
import { toast } from 'sonner';
// Import the shared Email Preview component
import { EmailPreview } from './EmailPreview';
import EmailStatisticsDashboard, {
  EmailStatisticsDetailsModal,
} from './EmailStatisticsDashboard';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');

// ============================================
// CUSTOM VALUES / MERGE TAGS CONFIGURATION
// ============================================
const MERGE_TAG_CATEGORIES = [
  {
    id: 'contact',
    name: 'Contact',
    icon: User,
    tokens: [
      { token: '{{contact.full_name}}', label: 'Full Name' },
      { token: '{{contact.first_name}}', label: 'First Name' },
      { token: '{{contact.last_name}}', label: 'Last Name' },
      { token: '{{contact.email}}', label: 'Email' },
      { token: '{{contact.phone}}', label: 'Phone' },
      { token: '{{contact.phone_raw}}', label: 'Phone (raw format)' },
      { token: '{{contact.company_name}}', label: 'Company Name' },
      { token: '{{contact.full_address}}', label: 'Full Address' },
      { token: '{{contact.street_address}}', label: 'Street Address' },
      { token: '{{contact.city}}', label: 'City' },
      { token: '{{contact.state}}', label: 'State' },
      { token: '{{contact.zip_code}}', label: 'ZIP Code' },
      { token: '{{contact.date_of_birth}}', label: 'Date of Birth' },
      { token: '{{contact.ssn_last4}}', label: 'SSN (Last 4)' },
      { token: '{{contact.portal_link}}', label: 'Portal Link' },
      { token: '{{contact.status}}', label: 'Status' },
      { token: '{{contact.created_date}}', label: 'Created Date' },
    ],
  },
  {
    id: 'company',
    name: 'Company',
    icon: () => <span className="text-xs">🏢</span>,
    tokens: [
      { token: '{{company.name}}', label: 'Company Name' },
      { token: '{{company.phone}}', label: 'Company Phone' },
      { token: '{{company.email}}', label: 'Company Email' },
      { token: '{{company.website}}', label: 'Website' },
      { token: '{{company.address}}', label: 'Full Address' },
    ],
  },
  {
    id: 'user',
    name: 'User',
    icon: User,
    tokens: [
      { token: '{{user.full_name}}', label: 'User Full Name' },
      { token: '{{user.first_name}}', label: 'User First Name' },
      { token: '{{user.email}}', label: 'User Email' },
      { token: '{{user.phone}}', label: 'User Phone' },
      { token: '{{user.signature}}', label: 'User Signature' },
    ],
  },
  {
    id: 'appointment',
    name: 'Appointment',
    icon: () => <span className="text-xs">📅</span>,
    tokens: [
      { token: '{{appointment.title}}', label: 'Title' },
      { token: '{{appointment.date}}', label: 'Date' },
      { token: '{{appointment.time}}', label: 'Time' },
      { token: '{{appointment.datetime}}', label: 'Date & Time' },
      { token: '{{appointment.duration}}', label: 'Duration' },
      { token: '{{appointment.location}}', label: 'Location' },
      { token: '{{appointment.meeting_link}}', label: 'Meeting Link' },
      { token: '{{appointment.cancel_link}}', label: 'Cancel Link' },
      { token: '{{appointment.reschedule_link}}', label: 'Reschedule Link' },
    ],
  },
  {
    id: 'calendar',
    name: 'Calendar',
    icon: () => <span className="text-xs">🗓️</span>,
    tokens: [
      { token: '{{calendar.name}}', label: 'Calendar Name' },
      { token: '{{calendar.booking_link}}', label: 'Booking Link' },
      { token: '{{calendar.timezone}}', label: 'Timezone' },
    ],
  },
  {
    id: 'message',
    name: 'Message',
    icon: Mail,
    tokens: [
      { token: '{{message.subject}}', label: 'Subject' },
      { token: '{{message.preview}}', label: 'Preview Text' },
      { token: '{{message.unsubscribe_link}}', label: 'Unsubscribe Link' },
      { token: '{{message.view_in_browser_link}}', label: 'View in Browser Link' },
    ],
  },
  {
    id: 'account',
    name: 'Account',
    icon: () => <span className="text-xs">⚙️</span>,
    tokens: [
      { token: '{{account.name}}', label: 'Agency Name' },
      { token: '{{account.phone}}', label: 'Agency Phone' },
      { token: '{{account.email}}', label: 'Agency Email' },
      { token: '{{account.website}}', label: 'Agency Website' },
      { token: '{{account.logo_url}}', label: 'Logo URL' },
      { token: '{{account.address}}', label: 'Agency Address' },
    ],
  },
  {
    id: 'right_now',
    name: 'Right now',
    icon: Clock,
    tokens: [
      { token: '{{now.date}}', label: 'Current Date' },
      { token: '{{now.time}}', label: 'Current Time' },
      { token: '{{now.datetime}}', label: 'Current Date & Time' },
      { token: '{{now.day_of_week}}', label: 'Day of Week' },
      { token: '{{now.month}}', label: 'Month' },
      { token: '{{now.year}}', label: 'Year' },
    ],
  },
  {
    id: 'credit_repair',
    name: 'Credit Repair',
    icon: () => <span className="text-xs">📊</span>,
    tokens: [
      { token: '{{dispute.status}}', label: 'Dispute Status' },
      { token: '{{dispute.round}}', label: 'Current Round' },
      { token: '{{dispute.items_count}}', label: 'Items Count' },
      { token: '{{dispute.next_step}}', label: 'Next Step' },
      { token: '{{credit.score_equifax}}', label: 'Equifax Score' },
      { token: '{{credit.score_experian}}', label: 'Experian Score' },
      { token: '{{credit.score_transunion}}', label: 'TransUnion Score' },
      { token: '{{credit.report_date}}', label: 'Report Date' },
    ],
  },
];

// ============================================
// MERGE TAGS BROWSER COMPONENT
// ============================================
const MergeTagsBrowser = ({ isOpen, onClose, onInsertToken, position = 'right' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('contact');
  const [copiedToken, setCopiedToken] = useState(null);

  if (!isOpen) return null;

  // Filter tokens based on search
  const filteredCategories = MERGE_TAG_CATEGORIES.map(category => ({
    ...category,
    tokens: category.tokens.filter(token =>
      token.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      token.token.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => searchQuery === '' || category.tokens.length > 0);

  const handleInsertToken = (token) => {
    onInsertToken(token);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 1500);
  };

  const handleCopyToken = (token, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(token);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 1500);
  };

  return (
    <div 
      className={cn(
        "absolute bg-white border border-slate-200 rounded-xl shadow-2xl z-[100] w-80 max-h-[420px] flex flex-col",
        position === 'right' ? 'right-0 top-8' : 'left-0 top-full mt-1'
      )}
      style={{ maxHeight: 'calc(100vh - 200px)' }}
      data-testid="merge-tags-browser"
    >
      {/* Header */}
      <div className="p-3 border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-900 text-sm">Custom Values</h4>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={14} className="text-slate-400" />
          </button>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search tokens..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
      </div>

      {/* Categories & Tokens */}
      <div className="flex-1 overflow-y-auto">
        {filteredCategories.map((category) => {
          const isExpanded = expandedCategory === category.id || searchQuery !== '';
          const IconComponent = category.icon;
          
          return (
            <div key={category.id} className="border-b border-slate-100 last:border-0">
              <button
                onClick={() => setExpandedCategory(isExpanded && searchQuery === '' ? null : category.id)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors"
              >
                <ChevronRight 
                  size={14} 
                  className={cn(
                    "text-slate-400 transition-transform",
                    isExpanded && "rotate-90"
                  )} 
                />
                <span className="w-5 h-5 flex items-center justify-center text-slate-500">
                  <IconComponent size={14} />
                </span>
                <span className="text-sm font-medium text-slate-700">{category.name}</span>
                <Badge variant="outline" className="ml-auto text-xs">
                  {category.tokens.length}
                </Badge>
              </button>
              
              {isExpanded && (
                <div className="pb-2">
                  {category.tokens.map((item) => (
                    <div
                      key={item.token}
                      onClick={() => handleInsertToken(item.token)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 pl-10 hover:bg-blue-50 text-left group cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleInsertToken(item.token)}
                    >
                      <span className="text-sm text-slate-600 flex-1">{item.label}</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => handleCopyToken(item.token, e)}
                          className="p-1 hover:bg-slate-200 rounded"
                          title="Copy token"
                        >
                          {copiedToken === item.token ? (
                            <CheckCircle2 size={12} className="text-emerald-500" />
                          ) : (
                            <Copy size={12} className="text-slate-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="p-2 border-t border-slate-200 bg-slate-50">
        <p className="text-xs text-slate-500 text-center">
          Click to insert at cursor position
        </p>
      </div>
    </div>
  );
};

// ============================================
// STATISTICS TAB COMPONENT (ALWAYS-ON DASHBOARD)
// ============================================
const EmailStatisticsTab = ({ stepId, stats }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fetchedStats, setFetchedStats] = useState(null);
  const [configStatus, setConfigStatus] = useState(null);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!stepId) {
      setFetchedStats(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const configRes = await fetch(`${API_URL}/api/webhooks/mailgun/config-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (configRes.ok) {
        setConfigStatus(await configRes.json());
      }

      const res = await fetch(`${API_URL}/api/webhooks/mailgun/stats/${stepId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) {
        setFetchedStats(await res.json());
      } else {
        setFetchedStats(null);
        setError('Statistics could not be refreshed.');
      }
    } catch (err) {
      console.error('Failed to fetch email stats:', err);
      setFetchedStats(null);
      setError('Statistics could not be refreshed.');
    } finally {
      setIsLoading(false);
    }
  }, [stepId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <>
      <EmailStatisticsDashboard
        variant="compact"
        stats={fetchedStats || stats}
        isLoading={isLoading}
        error={error}
        onRetry={fetchStats}
        trackingReady={configStatus ? Boolean(configStatus.is_tracking_ready) : true}
        onOpenDetails={() => setDetailsOpen(true)}
      />
      <EmailStatisticsDetailsModal
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        stats={fetchedStats || stats}
        isLoading={isLoading}
        error={error}
        onRetry={fetchStats}
      />
    </>
  );
};

// ============================================
// TOOLTIP COMPONENT
// ============================================
const Tooltip = ({ children, text }) => {
  return (
    <div className="relative group">
      {children}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
        {text}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
      </div>
    </div>
  );
};

// ============================================
// MAIN EMAIL NODE EDITOR COMPONENT
// ============================================
const EmailNodeEditor = ({ 
  isOpen, 
  onClose, 
  step, 
  onSave,
  channelStatus,
  onNavigateToSettings,
  workflowStatus = 'draft',
  workflowId,
  token,
  agencyId,
}) => {
  // State
  const [activeTab, setActiveTab] = useState('compose'); // 'compose' | 'statistics'
  const [config, setConfig] = useState({
    actionName: '',
    subject: '',
    preheader: '',
    fromName: '',
    fromEmail: '',
    replyTo: '',
    cc: '',
    bcc: '',
    bodyType: 'simple', // 'simple' | 'builder' | 'template'
    body: '',
    templateId: '',
    // Additional settings
    trackClicks: true,
    utmTracking: false,
    utmSource: '',
    utmMedium: 'email',
    utmCampaign: '',
    addTags: [],
    tagInput: '',
    // Attachments
    attachments: [],
  });
  
  const [showMergeTagsBrowser, setShowMergeTagsBrowser] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(false);
  const [testEmails, setTestEmails] = useState('');
  const [testEmailError, setTestEmailError] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testSentSuccess, setTestSentSuccess] = useState(false);
  const [showWriteWithAIModal, setShowWriteWithAIModal] = useState(false);
  
  // Refs for cursor-aware insertion
  const subjectRef = useRef(null);
  const preheaderRef = useRef(null);
  const bodyRef = useRef(null);
  const richEditorRef = useRef(null);
  const cursorPositionRef = useRef({});

  // Initialize from step config
  useEffect(() => {
    if (step?.config) {
      setConfig(prev => ({
        ...prev,
        actionName: step.config.actionName || step.config.subject || '',
        subject: step.config.subject || '',
        preheader: step.config.preheader || '',
        fromName: step.config.fromName || '',
        fromEmail: step.config.fromEmail || '',
        replyTo: step.config.replyTo || '',
        cc: step.config.cc || '',
        bcc: step.config.bcc || '',
        bodyType: step.config.bodyType || 'simple',
        body: step.config.body || '',
        templateId: step.config.templateId || '',
        trackClicks: step.config.trackClicks !== false,
        utmTracking: step.config.utmTracking || false,
        utmSource: step.config.utmSource || '',
        utmMedium: step.config.utmMedium || 'email',
        utmCampaign: step.config.utmCampaign || '',
        addTags: step.config.addTags || [],
        attachments: step.config.attachments || [],
      }));
    }
  }, [step]);

  // Insert token at cursor position - MUST be before early return
  const insertToken = useCallback((token) => {
    const fieldName = activeField;
    if (!fieldName) return;

    const fieldMap = {
      subject: { key: 'subject', ref: subjectRef },
      preheader: { key: 'preheader', ref: preheaderRef },
      body: { key: 'body', ref: bodyRef },
    };

    const field = fieldMap[fieldName];
    if (!field) return;

    setConfig(prev => {
      const currentValue = prev[field.key] || '';
      const cursorPos = cursorPositionRef.current[fieldName] ?? currentValue.length;
      
      const newValue = 
        currentValue.slice(0, cursorPos) + 
        token + 
        currentValue.slice(cursorPos);

      // Update cursor position for next insertion
      cursorPositionRef.current[fieldName] = cursorPos + token.length;

      return { ...prev, [field.key]: newValue };
    });

    setShowMergeTagsBrowser(false);
  }, [activeField]);

  const handleUseAIEmail = useCallback((generatedEmail) => {
    const content = formatEmailForEditor(generatedEmail, false);
    setConfig((prev) => ({ ...prev, body: content }));
    richEditorRef.current?.setHtml(content);
  }, []);

  // Early return after all hooks
  if (!isOpen) return null;

  const emailChannelReady = channelStatus?.email?.ready;
  const emailEntitled = channelStatus?.email?.entitled;
  const channelReason = channelStatus?.email?.reason;

  // Calculate live counts
  const charCount = stripHtmlForCount(config.body).length;
  const wordCount = countWords(config.body);

  // Store cursor position when focusing on a field
  const handleFieldFocus = (fieldName, ref) => {
    setActiveField(fieldName);
    if (ref?.current) {
      cursorPositionRef.current[fieldName] = ref.current.selectionStart || 0;
    }
  };

  // Update cursor position on selection change
  const handleSelectionChange = (fieldName, ref) => {
    if (ref?.current) {
      cursorPositionRef.current[fieldName] = ref.current.selectionStart || 0;
    }
  };

  // Validate test emails
  const validateTestEmails = (emails) => validateTestEmailInput(emails);

  // Send test email
  const handleSendTestEmail = async () => {
    const error = validateTestEmails(testEmails);
    if (error) {
      setTestEmailError(error);
      return;
    }
    
    if (!emailChannelReady) {
      setTestEmailError('Email channel is not available');
      return;
    }

    const recipients = parseTestEmailInput(testEmails);
    setTestEmailError('');
    setTestSentSuccess(false);
    setIsSendingTest(true);

    try {
      const { response, data } = await sendAutomationTestEmail({
        apiBase: API_URL,
        token: localStorage.getItem('token'),
        recipients,
        subject: config.subject,
        bodyHtml: config.body,
        bodyText: config.body.replace(/<[^>]*>/g, ''),
        fromName: config.fromName || 'FundElite',
        fromEmail: config.fromEmail,
        workflowId,
        actionId: stepId,
      });

      if (isAutomationTestEmailSuccess(response, data)) {
        setTestSentSuccess(true);
        setTestEmailError('');
        toast.success(formatTestEmailSuccessMessage(data, recipients));
        setTimeout(() => setTestSentSuccess(false), 5000);
      } else {
        const message = formatTestEmailErrorMessage(data);
        setTestEmailError(message);
        toast.error(message);
      }
    } catch (err) {
      console.error('Test email error:', err);
      const message = 'Failed to send test email. Please try again.';
      setTestEmailError(message);
      toast.error(message);
    } finally {
      setIsSendingTest(false);
    }
  };

  // Add tag
  const handleAddTag = () => {
    if (config.tagInput.trim() && !config.addTags.includes(config.tagInput.trim())) {
      setConfig(prev => ({
        ...prev,
        addTags: [...prev.addTags, prev.tagInput.trim()],
        tagInput: '',
      }));
    }
  };

  // Remove tag
  const handleRemoveTag = (tagToRemove) => {
    setConfig(prev => ({
      ...prev,
      addTags: prev.addTags.filter(tag => tag !== tagToRemove),
    }));
  };

  const handleSave = () => {
    onSave({
      ...step,
      config: {
        ...config,
        configured: true,
      }
    });
    onClose();
  };

  const handleDelete = () => {
    // Would trigger step deletion in parent
    onClose();
  };

  return (
    <>
    <div
      className="fixed right-0 top-0 h-screen w-[560px] bg-white border-l border-slate-200 shadow-xl z-40 flex flex-col"
      data-testid="email-node-editor"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-blue-500 to-blue-600">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
            <Mail size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Send Email</h3>
            <p className="text-xs text-blue-100">Configure email action</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
        >
          <X size={18} className="text-white" />
        </button>
      </div>

      {/* Channel Status Banner */}
      {!emailEntitled ? (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="font-medium text-red-800">Email Service Not Enabled</p>
              <p className="text-sm text-red-700 mt-1">
                Your plan doesn't include email services. Upgrade to send emails from workflows.
              </p>
              <Button
                size="sm"
                className="mt-2 bg-red-500 hover:bg-red-600 text-white"
                onClick={() => onNavigateToSettings?.('billing')}
              >
                <ArrowUpRight size={14} className="mr-1" />
                Upgrade / Add Features
              </Button>
            </div>
          </div>
        </div>
      ) : !emailChannelReady ? (
        <div className="p-4 bg-amber-50 border-b border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="font-medium text-amber-800">Email Service Not Configured</p>
              <p className="text-sm text-amber-700 mt-1">
                {channelReason === 'not_connected' 
                  ? 'Connect your email service (Mailgun) to send emails from workflows.'
                  : channelReason === 'domain_not_verified'
                  ? 'Your sending domain is not verified. Verify it to send emails.'
                  : 'Complete your email service configuration to send emails.'}
              </p>
              <Button
                size="sm"
                className="mt-2 bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => onNavigateToSettings?.('communication')}
              >
                <ArrowUpRight size={14} className="mr-1" />
                Open Email Services
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2">
          <CheckCircle size={16} className="text-emerald-500" />
          <span className="text-sm text-emerald-700">Email service connected and ready</span>
        </div>
      )}

      {/* Tabs - Compose | Preview | Statistics */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setActiveTab('compose')}
          className={cn(
            "flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5",
            activeTab === 'compose'
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          )}
          data-testid="email-tab-compose"
        >
          <Edit3 size={14} />
          Compose
        </button>
        <button
          onClick={() => setActiveTab('preview')}
          className={cn(
            "flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5",
            activeTab === 'preview'
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          )}
          data-testid="email-tab-preview"
        >
          <Eye size={14} />
          Preview
        </button>
        <button
          onClick={() => setActiveTab('statistics')}
          className={cn(
            "flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5",
            activeTab === 'statistics'
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          )}
          data-testid="email-tab-statistics"
        >
          <BarChart3 size={14} />
          Statistics
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'compose' ? (
          <div className="p-4 space-y-5">
            {/* Action Name */}
            <div className="space-y-2">
              <Label className="text-slate-600">Action Name</Label>
              <Input
                placeholder="e.g., Welcome Email"
                value={config.actionName}
                onChange={(e) => setConfig(prev => ({ ...prev, actionName: e.target.value }))}
                disabled={!emailChannelReady}
                className="bg-white"
              />
              <p className="text-xs text-slate-500">Internal name to identify this email step</p>
            </div>

            {/* Sender Info */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
                <User size={16} className="text-slate-400" />
                Sender Information
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-600 text-xs">From Name</Label>
                  <MergeFieldInput
                    placeholder="e.g., My Credit Agency"
                    value={config.fromName}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, fromName: value }))}
                    disabled={!emailChannelReady}
                    inputClassName="bg-white h-9"
                    data-testid="from-name-input"
                    token={token}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-xs">From Email</Label>
                  <MergeFieldInput
                    placeholder="e.g., noreply@agency.com"
                    value={config.fromEmail}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, fromEmail: value }))}
                    disabled={!emailChannelReady}
                    inputClassName="bg-white h-9"
                    data-testid="from-email-input"
                    token={token}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-600 text-xs">Reply-To</Label>
                  <MergeFieldInput
                    placeholder="support@..."
                    value={config.replyTo}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, replyTo: value }))}
                    disabled={!emailChannelReady}
                    inputClassName="bg-white h-9"
                    token={token}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-xs">Cc</Label>
                  <MergeFieldInput
                    placeholder="Optional"
                    value={config.cc}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, cc: value }))}
                    disabled={!emailChannelReady}
                    inputClassName="bg-white h-9"
                    token={token}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-xs">Bcc</Label>
                  <MergeFieldInput
                    placeholder="Optional"
                    value={config.bcc}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, bcc: value }))}
                    disabled={!emailChannelReady}
                    inputClassName="bg-white h-9"
                    token={token}
                  />
                </div>
              </div>
            </div>

            {/* Subject & Preheader */}
            <div className="space-y-3 relative">
              <div className="space-y-2 relative">
                <Label className="text-slate-600">Subject Line</Label>
                <MergeFieldInput
                  placeholder="e.g., Welcome to {{contact.first_name}}!"
                  value={config.subject}
                  onValueChange={(value) => setConfig(prev => ({ ...prev, subject: value }))}
                  disabled={!emailChannelReady}
                  inputClassName="bg-white"
                  data-testid="subject-input"
                  token={token}
                />
              </div>
              
              <div className="space-y-2 relative">
                <Label className="text-slate-600 text-xs">Pre-header (Optional)</Label>
                <MergeFieldInput
                  placeholder="Preview text shown in inbox"
                  value={config.preheader}
                  onValueChange={(value) => setConfig(prev => ({ ...prev, preheader: value }))}
                  disabled={!emailChannelReady}
                  inputClassName="bg-white h-9 text-sm"
                  data-testid="preheader-input"
                  token={token}
                />
              </div>
            </div>

            {/* Body Type Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setConfig(prev => ({ ...prev, bodyType: 'simple' }))}
                className={cn(
                  'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors',
                  config.bodyType === 'simple'
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                )}
              >
                Quick Compose
              </button>
              <button
                onClick={() => setConfig(prev => ({ ...prev, bodyType: 'builder' }))}
                className={cn(
                  'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors',
                  config.bodyType === 'builder'
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                )}
              >
                Smart Builder
              </button>
              <button
                onClick={() => setConfig(prev => ({ ...prev, bodyType: 'template' }))}
                className={cn(
                  'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors',
                  config.bodyType === 'template'
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                )}
              >
                Template
              </button>
            </div>

            {/* Email Body */}
            {config.bodyType === 'simple' && (
              <div className="space-y-0 relative">
                <Label className="text-slate-600 mb-2 block">Email Body</Label>

                <RichEmailEditor
                  ref={richEditorRef}
                  value={config.body}
                  disabled={!emailChannelReady}
                  onChange={(html) => setConfig((prev) => ({ ...prev, body: html }))}
                  onWriteWithAIClick={() => setShowWriteWithAIModal(true)}
                  attachments={config.attachments}
                  token={token}
                  footer={(
                    <div className="flex items-center justify-between p-2 bg-slate-50 rounded-b-lg border border-t-0 border-slate-200">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-purple-600 hover:bg-purple-50"
                        disabled={!emailChannelReady}
                        onClick={() => setShowWriteWithAIModal(true)}
                        data-testid="write-with-ai-button"
                      >
                        <Sparkles size={12} className="mr-1" />
                        Write with AI
                      </Button>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{charCount.toLocaleString()} characters</span>
                        <span className="text-slate-300">|</span>
                        <span>{wordCount.toLocaleString()} words</span>
                      </div>
                    </div>
                  )}
                />
              </div>
            )}

            {config.bodyType === 'builder' && (
              <div className="p-6 bg-slate-50 rounded-lg border border-slate-200 text-center">
                <Sparkles className="mx-auto text-purple-400 mb-2" size={24} />
                <p className="text-sm text-slate-600">Smart Builder</p>
                <p className="text-xs text-slate-500 mt-1">Drag-and-drop email builder coming soon</p>
              </div>
            )}

            {config.bodyType === 'template' && (
              <div className="space-y-2">
                <Label className="text-slate-600">Select Template</Label>
                <select
                  value={config.templateId}
                  onChange={(e) => setConfig(prev => ({ ...prev, templateId: e.target.value }))}
                  disabled={!emailChannelReady}
                  className="w-full h-10 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
                >
                  <option value="">Choose a template...</option>
                  <option value="welcome">Welcome Email</option>
                  <option value="document_request">Document Request</option>
                  <option value="dispute_update">Dispute Status Update</option>
                  <option value="payment_reminder">Payment Reminder</option>
                  <option value="appointment_confirm">Appointment Confirmation</option>
                </select>
                
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 mt-3">
                  <div className="flex items-center gap-2 text-slate-500 text-sm">
                    <Eye size={14} />
                    <span>Template preview will appear here</span>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Settings Accordion */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowAdditionalSettings(!showAdditionalSettings)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                data-testid="additional-settings-toggle"
              >
                <div className="flex items-center gap-2">
                  <Settings size={16} className="text-slate-500" />
                  <span className="font-medium text-slate-700 text-sm">Additional Settings</span>
                </div>
                <ChevronDown 
                  size={16} 
                  className={cn(
                    "text-slate-400 transition-transform",
                    showAdditionalSettings && "rotate-180"
                  )}
                />
              </button>
              
              {showAdditionalSettings && (
                <div className="p-4 space-y-4 border-t border-slate-200" data-testid="additional-settings-content">
                  {/* Track Clicks */}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.trackClicks}
                      onChange={(e) => setConfig(prev => ({ ...prev, trackClicks: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <MousePointerClick size={14} className="text-slate-400" />
                        Track Clicks
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Track when recipients click links in this email
                      </p>
                    </div>
                  </label>

                  {/* UTM Tracking */}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.utmTracking}
                      onChange={(e) => setConfig(prev => ({ ...prev, utmTracking: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <ExternalLink size={14} className="text-slate-400" />
                        UTM Tracking
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Automatically append UTM parameters to links for analytics tracking
                      </p>
                      {config.utmTracking && (
                        <div className="mt-3 space-y-2">
                          <div className="grid grid-cols-3 gap-2">
                            <Input
                              placeholder="Source"
                              value={config.utmSource}
                              onChange={(e) => setConfig(prev => ({ ...prev, utmSource: e.target.value }))}
                              className="h-8 text-xs"
                            />
                            <Input
                              placeholder="Medium"
                              value={config.utmMedium}
                              onChange={(e) => setConfig(prev => ({ ...prev, utmMedium: e.target.value }))}
                              className="h-8 text-xs"
                            />
                            <Input
                              placeholder="Campaign"
                              value={config.utmCampaign}
                              onChange={(e) => setConfig(prev => ({ ...prev, utmCampaign: e.target.value }))}
                              className="h-8 text-xs"
                            />
                          </div>
                          <p className="text-xs text-slate-400">
                            Or <button className="text-blue-500 hover:underline">manage default UTM parameters</button>
                          </p>
                        </div>
                      )}
                    </div>
                  </label>

                  {/* Add Tags */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Tag size={14} className="text-slate-400" />
                      <p className="text-sm font-medium text-slate-700">Add Tags</p>
                    </div>
                    <p className="text-xs text-slate-500">
                      Apply tags to contacts when this email is sent
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter tag name"
                        value={config.tagInput}
                        onChange={(e) => setConfig(prev => ({ ...prev, tagInput: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                        className="h-8 text-sm flex-1"
                      />
                      <Button size="sm" variant="outline" onClick={handleAddTag} className="h-8">
                        Add
                      </Button>
                    </div>
                    {config.addTags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {config.addTags.map((tag) => (
                          <Badge 
                            key={tag} 
                            variant="outline" 
                            className="bg-blue-50 text-blue-700 border-blue-200 pr-1"
                          >
                            {tag}
                            <button 
                              onClick={() => handleRemoveTag(tag)}
                              className="ml-1 p-0.5 hover:bg-blue-100 rounded"
                            >
                              <X size={10} />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Test Email Section */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3" data-testid="test-email-section">
              <div className="flex items-center gap-2">
                <Send size={16} className="text-slate-500" />
                <h4 className="font-medium text-slate-700 text-sm">Test Email</h4>
              </div>
              
              <div className="space-y-2">
                <Input
                  placeholder="Enter email addresses (comma-separated)"
                  value={testEmails}
                  onChange={(e) => {
                    setTestEmails(e.target.value);
                    setTestEmailError('');
                  }}
                  disabled={!emailChannelReady}
                  className="bg-white"
                />
                {testEmailError && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {testEmailError}
                  </p>
                )}
                {testSentSuccess && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle size={12} />
                    Test email sent successfully!
                  </p>
                )}
              </div>
              
              <Button
                size="sm"
                variant="outline"
                onClick={handleSendTestEmail}
                disabled={!emailChannelReady || isSendingTest || !testEmails.trim()}
                className="w-full"
                data-testid="send-test-email-btn"
              >
                {isSendingTest ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={14} className="mr-2" />
                    Send Test Mail
                  </>
                )}
              </Button>
              
              {!emailChannelReady && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  Test sending unavailable: Email channel not configured
                </p>
              )}
            </div>

            {/* Info Box */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2">
                <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  Emails will be sent from your configured email service. Use custom values to personalize each message. Test your email before publishing the workflow.
                </p>
              </div>
            </div>
          </div>
        ) : activeTab === 'preview' ? (
          /* Preview Tab - Uses Shared EmailPreview Component */
          <EmailPreview
            fromName={config.fromName}
            fromEmail={config.fromEmail}
            replyTo={config.replyTo}
            cc={config.cc}
            bcc={config.bcc}
            subject={config.subject}
            preheader={config.preheader}
            bodyHtml={config.body}
            token={token}
          />
        ) : (
          <EmailStatisticsTab
            stepId={step?.id}
            stats={step?.stats}
          />
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:bg-red-50 hover:text-red-700">
            <Trash2 size={14} className="mr-1" />
            Delete
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
        <Button 
          onClick={handleSave}
          disabled={!emailChannelReady || !config.subject}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Save size={16} className="mr-2" />
          Save Action
        </Button>
      </div>
    </div>

    <WriteEmailWithAIModal
      isOpen={showWriteWithAIModal}
      onClose={() => setShowWriteWithAIModal(false)}
      onUseEmail={handleUseAIEmail}
      workflowId={workflowId}
      stepId={step?.id || step?._id}
      existingEmail={config.body}
      availableVariables={[
        '{{contact.first_name}}',
        '{{contact.email}}',
        '{{company.name}}',
        '{{reset_link}}',
        '{{client_portal_contact.login_url_magic_link}}',
        '{{custom_values.promotion_name}}',
        '{{custom_values.marketing_website_booking_page_url}}',
        '{{phoneCall.toCity}}',
      ]}
      token={token}
      isPlainText={false}
    />
    </>
  );
};

export default EmailNodeEditor;
